<!--

	Displays a content of a lesson, such as a Video or a Game.

-->
<template>
	<div class="mia-Lightbox">
		<div class="modal modal-centered fade in" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
			<div class="modal-dialog" role="document">
				<div v-if="showCrossInverted" class="mia-Cross--inverted" />
				<div
					v-click-outside="{
						handler: onClickOutside,
						include
					}"
					class="mia-modalContent"
				>
					<slot />
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade in" />
	</div>
</template>
<script lang='ts'>
import { Component, Vue, Prop, toNative } from "vue-facing-decorator";
import { ClickOutside } from "vuetify/directives";
import ClickOutsideHelper from "@/utils/ClickOutsideHelper";

@Component({
	emits: ["close"],
	directives: {
		ClickOutside,
	},
})
export class Lightbox extends Vue {
	@Prop({ required: false, type: Boolean, default: true }) showCrossInverted!: boolean;

	include = () => ClickOutsideHelper.GetExcludedElements();

	onClickOutside(): void {
		this.turnOffTTS();
		this.$emit("close");
	}

	turnOffTTS(): void {
		window.soundManager.stopAll();
	}
}

export default toNative(Lightbox);
</script>
<style lang="scss">
.mia-Lightbox {
	.mia-Cross--inverted {
		position: absolute;
		// Cross should appear in front of content modal.
		z-index: theme.$mia-zIndex-1--foreground;
		top: 0;
		right: theme.$mia-lightbox-cross-position;
		cursor: pointer;

		@media (orientation: portrait) {
			top: theme.$mia-lightbox-cross-position;
			right: 0;
		}

		@media (min-width: theme.$screen-md-min) {
			right: theme.$mia-lightbox-cross-position;
		}
	}

	.modal.in .modal-dialog {
		width: 60%;
		max-width: theme.$mia-lightbox-modal-dialog-max-width;
		padding: 0;

		@media (min-width: theme.$screen-sm-min) and (max-width: theme.$screen-sm-max) {
			width: 90%;
		}

		@media (max-width: theme.$screen-xs-max) and (orientation: portrait) {
			width: 100%;
		}

		// For screens with a bigger aspect ratio than Galaxy Fold has (also for cases when the address bar is open)
		@media (max-aspect-ratio: 300/100) {
			width: calc((16 / 9) / (300 / 100) * 100%);
		}

		// For screens such as Galaxy Fold
		@media (max-aspect-ratio: 234/100) {
			width: calc((16 / 9) / (23 / 100) * 100%);
		}

		// For screens such as Pixel 2XL and iPhone X
		@media (max-aspect-ratio: 216/100) {
			width: calc((16 / 9) / (216 / 100) * 100%);
		}

		@media (max-aspect-ratio: 16/9) {
			width: 90%;
		}
	}

	.mia-rotateMessage {
		display: none;
		text-align: center;
		color: theme.$mia-color-white;
		max-width: theme.$mia-lightbox-rotateMessage-maxWidth;
		margin: 0 auto;
		padding-top: theme.$mia-padding-panel-large;

		@media (max-width: theme.$screen-xs-max) and (orientation: portrait) {
			display: block;
		}
	}

	&.mia-Lightbox-11-9 {
		// For screens with a bigger aspect ratio than Galaxy Fold has (also for cases when the address bar is open)
		@media (max-aspect-ratio: 300/100) {
			.modal.in .modal-dialog {
				width: calc((11 / 9) / (300 / 100) * 100%);
			}
		}

		// For screens such as Galaxy Fold
		@media (max-aspect-ratio: 234/100) {
			.modal.in .modal-dialog {
				width: calc((11 / 9) / (234 / 100) * 100%);
			}
		}

		// For screens such as Pixel 2XL and iPhone X
		@media (max-aspect-ratio: 216/100) {
			.modal.in .modal-dialog {
				width: calc((11 / 9) / (216 / 100) * 100%);
			}
		}

		// For screens such as iPhone 5/SE/6/7/8
		@media (max-aspect-ratio: 178/100) {
			.modal.in .modal-dialog {
				width: calc((11 / 9) / (178 / 100) * 100%);
			}
		}

		// For screens such as iPadPro
		@media (max-aspect-ratio: 134/100) {
			.modal.in .modal-dialog {
				width: calc((11 / 9) / (134 / 100) * 100%);
			}
		}

		@media (max-aspect-ratio: 11/9) {
			.modal.in .modal-dialog {
				width: 90%;
			}
		}
	}

	&.mia-Lightbox-fullscreen {
		.modal-dialog .mia-modalContent {
			height: calc(100vh - #{theme.$mia-lightbox-fullscreen-margin});
		}

		.mia-Cross--inverted {
			display: none;
		}

		@media (max-width: theme.$screen-xs-max) {
			.modal {
				// Override default bootstrap modal padding.
				padding: 0 !important;

				.modal-dialog {
					width: 100%;
					height: 100%;
				}
			}
		}
	}
}
</style>
